/**
 * Pure derivations for the lot page deal view, driven by the model's layout
 * (`deal-model-layout-v1`): result lookups, the current value and source of
 * each input, the verdict reason sentence, the check rows, the input tiers,
 * the bid sentence and the exact draft <-> override conversions of the Adjust
 * rail. Nothing here renders, fetches or uses a host float for a value; the
 * exact formatters of format.ts own every displayed number.
 */
import { isLosslessNumber } from 'lossless-json'
import type { DealModelLayout, DealModelLayoutCheck, DealModelLayoutInput, DealModelLayoutTier, LayoutComparator } from '../../../api/dealModelLayout'
import type { DealModelValueDescriptor } from '../../../api/dealModelTypes'
import type { BidEvaluation, ModelPresentation, PresentationOutput } from '../../../api/financial'
import { normalizeSettingsDecimal, settingsOptionLabel, shiftSettingsDecimal } from '../../settings/settingsFieldPresentation'
import { isPersonalControlOverride, isStockFinancialModel } from '../../deal-models/propertyOverrides'
import { bidStateLabel, formatBidPence, formatOutput, humanize, valueAt, type OutputScalar } from './format'

/* ---------------------------------------------------------------- lookups */

export interface ResultLookups {
  outputs: Map<string, PresentationOutput>
  /** Saved value by JSON pointer, read once. */
  byPointer: Map<string, OutputScalar | undefined>
  value: (outputId: string) => OutputScalar | undefined
  formatted: (outputId: string) => string
}

/** One pass over the presentation: outputId -> descriptor, pointer -> saved value. */
export function resultLookups(presentation: ModelPresentation, result: Record<string, unknown> | null | undefined): ResultLookups {
  const outputs = new Map(presentation.outputs.map(output => [output.outputId, output]))
  const byPointer = new Map<string, OutputScalar | undefined>()
  for (const output of presentation.outputs) if (!byPointer.has(output.pointer)) byPointer.set(output.pointer, result ? valueAt(result, output.pointer) : undefined)
  const value = (outputId: string) => { const output = outputs.get(outputId); return output ? byPointer.get(output.pointer) : undefined }
  const formatted = (outputId: string) => { const output = outputs.get(outputId); return output ? formatOutput(byPointer.get(output.pointer), output) : '—' }
  return { outputs, byPointer, value, formatted }
}

/* ------------------------------------------------------- input semantics */

const SEMANTIC_TYPES: ReadonlyArray<DealModelValueDescriptor['semanticType']> = ['money', 'moneyPence', 'decimal', 'ratio', 'percentage', 'integer', 'count', 'months', 'days', 'areaSqm', 'boolean', 'enum', 'string', 'date']
const INTEGRAL_KINDS = new Set(['integer', 'count', 'months', 'days'])

/** Integer pence carried by an integer input (`purchaseBidPence`), or a declared moneyPence input. */
export function isPenceInput(input: Pick<DealModelLayoutInput, 'key' | 'unit' | 'semanticType'>): boolean {
  const kind = input.semanticType.kind
  return kind === 'moneyPence' || (kind === 'integer' && (input.unit === 'pence' || /Pence$/.test(input.key)))
}

/** The presentation descriptor an input value is formatted with. */
export function inputDescriptor(input: DealModelLayoutInput): Pick<PresentationOutput, 'semanticType' | 'unit' | 'enumValues'> {
  const kind = input.semanticType.kind
  const semanticType = isPenceInput(input) ? 'moneyPence' : (SEMANTIC_TYPES as readonly string[]).includes(kind) ? kind as DealModelValueDescriptor['semanticType'] : 'string'
  // Money, ratios and percentages carry their unit in the format itself.
  const unit = ['money', 'moneyPence', 'ratio', 'percentage', 'boolean', 'enum', 'string', 'date'].includes(semanticType) ? null : input.unit
  return { semanticType, unit, enumValues: input.semanticType.enumValues ?? [] }
}

/** A transported value as an exact token: lossless and host numbers become decimal text. */
export function valueToken(value: unknown): OutputScalar | undefined {
  if (value === null) return null
  if (typeof value === 'boolean' || typeof value === 'string') return value
  if (isLosslessNumber(value)) return value.toString()
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return undefined
}

export function formatInputValue(input: DealModelLayoutInput, value: unknown): string {
  const token = valueToken(value)
  const descriptor = inputDescriptor(input)
  if (descriptor.semanticType === 'enum' && typeof token === 'string' && descriptor.enumValues.includes(token)) return settingsOptionLabel(token)
  if (typeof token === 'string' && !['enum', 'string', 'date', 'boolean'].includes(descriptor.semanticType)) {
    // Stored overrides and authored defaults may carry trailing zeros; the exact formatter wants the canonical lexeme.
    return formatOutput(normalizeSettingsDecimal(token) ?? token, descriptor)
  }
  return formatOutput(token, descriptor)
}

/* ---------------------------------------------------- input value sources */

export type InputSource = 'override' | 'listing' | 'bid' | 'valuation' | 'estimate' | 'works' | 'setting' | 'fact' | 'default' | 'unknown'

export const SOURCE_LABEL: Record<InputSource, string> = {
  override: 'your override',
  listing: 'listing',
  bid: 'maximum bid',
  valuation: 'valuation',
  estimate: 'your estimate',
  works: 'works estimate',
  setting: 'your setting',
  fact: 'property data',
  default: 'default',
  unknown: 'not loaded',
}

export interface InputContext {
  /** The model these inputs belong to: stock models leave works budgets and the GDV to the personal controls. */
  logicalModelId?: string
  /** Saved property overrides keyed by propertyOverrideKey (GET overrides `values`); null when unknown. */
  overrides: Record<string, unknown> | null
  /** The effective model settings keyed by userSettingKey (see `effectiveSettings`); null when unknown. */
  settings: Record<string, unknown> | null
  /** Listing facts; null when unknown (not read yet or unavailable), each price null when the listing has none. */
  listing: { calculationPricePence: number | null; marketedPricePence: number | null } | null
  /** Display-only acquisition basis. Null explicitly means no valid maximum exists. */
  calculationBidPence?: string | null
  /** GDV facts; null when unknown. */
  gdv: { personalPence: number | null; systemPence: number | null } | null
  /** The parsed property refurbishment read, walked by `diligence.refurbishment.*` fact paths; null when unknown. */
  refurbishment: unknown
  /** Stock models: the personal works budget that replaces the works fact for `refurbCost`. */
  selectedWorksPence?: number | null
  /**
   * The saved result may predate these values (a newer calculation is being
   * prepared): input values are then not paired with saved outputs.
   */
  resultStale?: boolean
}

export interface ResolvedInput { value: OutputScalar | undefined; source: InputSource }

const own = (values: Record<string, unknown> | null, key: string | null): boolean => Boolean(values && key && Object.hasOwn(values, key))
const UNKNOWN: ResolvedInput = { value: undefined, source: 'unknown' }

/** Exact equality of two transported setting values (decimal text and numbers compare by value). */
function sameSetting(left: unknown, right: unknown): boolean {
  const a = valueToken(left), b = valueToken(right)
  if (a === undefined || b === undefined) return false
  if (typeof a === 'string' && typeof b === 'string') { const order = compareDecimal(a, b); if (order !== null) return order === 0 }
  return a === b
}

/**
 * The settings a calculation of this model reads, as the server's mirror
 * builds them: the user's default assumptions, then the user's own settings
 * for this model (later wins), only for the layout's declared setting keys,
 * and without values equal to the input's authored default. Null when either
 * read is unknown; `modelSettings` undefined means the model has none.
 */
export function effectiveSettings(layout: DealModelLayout, userSettings: Record<string, unknown> | null, modelSettings: Record<string, unknown> | null | undefined): Record<string, unknown> | null {
  if (userSettings === null || modelSettings === null) return null
  const merged: Record<string, unknown> = {}
  const seen = new Set<string>()
  for (const input of layout.inputs) {
    const key = input.userSettingKey
    if (!key || seen.has(key)) continue
    seen.add(key)
    for (const source of [userSettings, modelSettings ?? {}]) {
      if (!Object.hasOwn(source, key)) continue
      if (sameSetting(source[key], input.defaultValue)) delete merged[key]
      else merged[key] = source[key]
    }
  }
  return merged
}

function penceToPounds(pence: number): string | undefined {
  if (!Number.isSafeInteger(pence)) return undefined
  const negative = pence < 0
  const absolute = BigInt(Math.abs(pence))
  return normalizeSettingsDecimal(`${negative ? '-' : ''}${absolute / 100n}.${(absolute % 100n).toString().padStart(2, '0')}`) ?? undefined
}

/** A fact read in integer pence, as the engine passes it: pounds only for a money input whose fact unit is pence. */
function factPenceValue(input: DealModelLayoutInput, pence: number): OutputScalar | undefined {
  if (!Number.isSafeInteger(pence)) return undefined
  return input.factUnit === 'pence' && input.semanticType.kind === 'money' ? penceToPounds(pence) : String(pence)
}

function walk(root: unknown, path: string): unknown {
  let value = root
  for (const part of path.split('.')) value = value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>)[part] : undefined
  return value
}

/** A fact read: unknown while its source is not read, else integer pence or null when the property has none. */
type FactRead = { known: false } | { known: true; pence: number | null; source: InputSource }

function readFact(input: DealModelLayoutInput, context: InputContext): FactRead | null {
  const fact = input.factPath
  if (!fact) return null
  if (fact.startsWith('card.listing.')) {
    if (!context.listing) return { known: false }
    const pence = fact === 'card.listing.calculationPricePence' ? context.listing.calculationPricePence : fact === 'card.listing.marketedPricePence' ? context.listing.marketedPricePence : undefined
    return pence === undefined ? { known: false } : { known: true, pence, source: 'listing' }
  }
  if (fact === 'card.valuation.gdvPence') return context.gdv ? { known: true, pence: context.gdv.systemPence, source: 'valuation' } : { known: false }
  if (fact.startsWith('diligence.refurbishment.')) {
    if (input.propertyOverrideKey === 'refurbCost' && context.selectedWorksPence !== undefined) return { known: true, pence: context.selectedWorksPence, source: 'works' }
    if (context.refurbishment === null || context.refurbishment === undefined) return { known: false }
    const raw = walk(context.refurbishment, fact.slice('diligence.refurbishment.'.length))
    return { known: true, pence: typeof raw === 'number' ? raw : null, source: 'works' }
  }
  // A fact this page does not read: its value is not known here.
  return { known: false }
}

/**
 * Where an input's current value comes from, in the engine's order: the
 * property override, then the setting (or, for the GDV fact, the personal
 * GDV), then the fact, then the model default. Stock models take the GDV only
 * from the personal GDV and its evidence. A read that is still unknown
 * (loading or failed) yields `unknown`, never a later fallback.
 */
export function resolveInput(input: DealModelLayoutInput, context: InputContext): ResolvedInput {
  if (context.calculationBidPence !== undefined && (input.key === 'purchaseBidPence' || input.factPath === 'card.listing.calculationPricePence')) {
    const pence = context.calculationBidPence
    return { value: pence === null ? null : input.semanticType.kind === 'money' ? shiftSettingsDecimal(pence, -2) : pence, source: 'bid' }
  }
  const modelId = context.logicalModelId
  const gdvFact = input.factPath === 'card.valuation.gdvPence'
  const stockGdv = gdvFact && modelId !== undefined && isStockFinancialModel(modelId)
  // Stock works budgets and GDV are never property overrides (the server refuses and omits them).
  const overrideKey = input.propertyOverrideKey && !(modelId !== undefined && isPersonalControlOverride(modelId, input)) ? input.propertyOverrideKey : null
  if (overrideKey && !stockGdv) {
    if (context.overrides === null) return UNKNOWN
    if (own(context.overrides, overrideKey)) return { value: valueToken(context.overrides[overrideKey]), source: 'override' }
  }
  if (input.userSettingKey && !stockGdv) {
    if (context.settings === null) return UNKNOWN
    if (own(context.settings, input.userSettingKey)) return { value: valueToken(context.settings[input.userSettingKey]), source: 'setting' }
  }
  if (gdvFact) {
    if (!context.gdv) return UNKNOWN
    if (context.gdv.personalPence !== null) return { value: factPenceValue(input, context.gdv.personalPence), source: 'estimate' }
  }
  const fact = readFact(input, context)
  if (fact && !fact.known) return UNKNOWN
  if (fact?.known && fact.pence !== null) return { value: factPenceValue(input, fact.pence), source: fact.source }
  // A missing fact falls through to the authored default, as in the engine.
  return { value: valueToken(input.defaultValue), source: 'default' }
}

/* ------------------------------------------------------------ comparisons */

const plainDecimal = /^-?\d+(?:\.\d+)?$/

/** Exact decimal order of two plain decimal tokens; null when either is not a plain decimal. */
export function compareDecimal(left: string, right: string): -1 | 0 | 1 | null {
  if (!plainDecimal.test(left) || !plainDecimal.test(right)) return null
  const scale = Math.max((left.split('.')[1] ?? '').length, (right.split('.')[1] ?? '').length)
  const scaled = (value: string) => {
    const negative = value.startsWith('-')
    const [whole = '0', fraction = ''] = value.replace(/^-/, '').split('.')
    const magnitude = BigInt(whole + fraction.padEnd(scale, '0'))
    return negative ? -magnitude : magnitude
  }
  const a = scaled(left), b = scaled(right)
  return a < b ? -1 : a > b ? 1 : 0
}

function satisfies(comparator: LayoutComparator, left: OutputScalar | undefined, right: OutputScalar | undefined): boolean | null {
  if (left === undefined || left === null || right === undefined || right === null) return null
  if (comparator === '==' || comparator === '!=') {
    const order = typeof left === 'string' && typeof right === 'string' ? compareDecimal(left, right) : null
    const equal = order !== null ? order === 0 : left === right
    return comparator === '==' ? equal : !equal
  }
  if (typeof left !== 'string' || typeof right !== 'string') return null
  const order = compareDecimal(left, right)
  if (order === null) return null
  return comparator === '>=' ? order >= 0 : comparator === '>' ? order > 0 : comparator === '<=' ? order <= 0 : order < 0
}

export const COMPARATOR_SYMBOL: Record<LayoutComparator, string> = { '>=': '≥', '>': '>', '<=': '≤', '<': '<', '==': '=', '!=': '≠' }
const MEETS_PHRASE: Record<LayoutComparator, string> = { '>=': 'is at least', '>': 'is above', '<=': 'is at most', '<': 'is below', '==': 'equals', '!=': 'differs from' }
const FAILS_PHRASE: Record<LayoutComparator, string> = { '>=': 'sits below', '>': 'sits at or below', '<=': 'sits above', '<': 'sits at or above', '==': 'differs from', '!=': 'equals' }
/** The requirement a check states, read out where the comparator symbol is shown. */
const REQUIRES_PHRASE: Record<LayoutComparator, string> = { '>=': 'needs at least', '>': 'needs more than', '<=': 'needs at most', '<': 'needs less than', '==': 'needs to equal', '!=': 'needs to differ from' }

interface Operand { raw: OutputScalar | undefined; text: string }

function operand(outputId: string | null, inputKey: string | null, layout: DealModelLayout, lookups: ResultLookups, context: InputContext): Operand | null {
  if (outputId) return lookups.outputs.has(outputId) ? { raw: lookups.value(outputId), text: lookups.formatted(outputId) } : null
  if (inputKey) {
    // A saved result being replaced may have used other input values.
    if (context.resultStale) return null
    const input = layout.inputs.find(candidate => candidate.key === inputKey)
    if (!input) return null
    const resolved = resolveInput(input, context)
    return { raw: resolved.value, text: formatInputValue(input, resolved.value) }
  }
  return null
}

function tierSentence(tier: DealModelLayoutTier, meets: boolean, layout: DealModelLayout, lookups: ResultLookups, context: InputContext): string | null {
  if (!tier.measureOutputId || !tier.comparator) return null
  const measure = lookups.outputs.get(tier.measureOutputId)
  const left = operand(tier.measureOutputId, null, layout, lookups, context)
  const right = operand(tier.thresholdOutputId, tier.thresholdOutputId ? null : tier.thresholdInputKey, layout, lookups, context)
  if (!measure || !left || !right || left.text === '—' || right.text === '—') return null
  // A sentence must agree with the saved values; a gate in a later `and` clause can decide a tier instead.
  if (satisfies(tier.comparator, left.raw, right.raw) !== meets) return null
  const phrase = meets ? MEETS_PHRASE[tier.comparator] : FAILS_PHRASE[tier.comparator]
  return `${measure.label} ${left.text} ${phrase} the ${right.text} ${humanize(tier.value).toLowerCase()} ${meets ? 'threshold' : 'band'}`
}

/**
 * Why the saved verdict is what it is, from the tiers' simple comparisons: the
 * better tier it missed when there is one, else the tier it met. Null when the
 * tiers carry no recognised comparison or the values do not support one.
 */
export function verdictReason(layout: DealModelLayout, verdict: string | null | undefined, lookups: ResultLookups, context: InputContext): string | null {
  const tiers = layout.verdict?.tiers ?? []
  const index = verdict ? tiers.findIndex(tier => tier.value === verdict) : -1
  if (index < 0) return null
  const better = index > 0 ? tiers[index - 1] : undefined
  if (better) { const missed = tierSentence(better, false, layout, lookups, context); if (missed) return missed }
  const own = tiers[index]
  return own ? tierSentence(own, true, layout, lookups, context) : null
}

/* ----------------------------------------------------------------- checks */

export interface CheckRow {
  outputId: string
  label: string
  passes: boolean | null
  left: string | null
  comparator: string | null
  /** The comparator in words, for assistive technology beside the symbol. */
  comparatorPhrase: string | null
  right: string | null
}

export function checkRow(outputId: string, layout: DealModelLayout, lookups: ResultLookups, context: InputContext, checks = checksById(layout)): CheckRow {
  const output = lookups.outputs.get(outputId)
  const value = lookups.value(outputId)
  const check = checks.get(outputId)
  const left = check ? operand(check.leftOutputId, check.leftOutputId ? null : check.leftInputKey, layout, lookups, context) : null
  const right = check ? operand(check.rightOutputId, check.rightOutputId ? null : check.rightInputKey, layout, lookups, context) : null
  const both = left && right && left.text !== '—' && right.text !== '—'
  return {
    outputId,
    label: output?.label ?? 'Check',
    passes: typeof value === 'boolean' ? value : null,
    left: both ? left.text : null,
    comparator: both && check?.comparator ? COMPARATOR_SYMBOL[check.comparator] : null,
    comparatorPhrase: both && check?.comparator ? REQUIRES_PHRASE[check.comparator] : null,
    right: both ? right.text : null,
  }
}

export function checksById(layout: DealModelLayout): Map<string, DealModelLayoutCheck> {
  return new Map(layout.checks.map(check => [check.outputId, check]))
}

/* ------------------------------------------------------------------ tiers */

export function inputsByTier(layout: DealModelLayout): Record<DealModelLayoutInput['tier'], DealModelLayoutInput[]> {
  const groups: Record<DealModelLayoutInput['tier'], DealModelLayoutInput[]> = { primary: [], assumption: [], target: [] }
  for (const input of layout.inputs) groups[input.tier].push(input)
  return groups
}

/** The KPI rows: the layout's own, else the presentation headline without the verdict; only rows the presentation carries. */
export function kpiOutputIds(layout: DealModelLayout, presentation: ModelPresentation): string[] {
  const known = new Set(presentation.outputs.map(output => output.outputId))
  const ids = layout.kpiOutputIds.length ? layout.kpiOutputIds : presentation.headlineOutputIds.filter(id => id !== layout.verdict?.outputId)
  return ids.filter((id, index) => known.has(id) && ids.indexOf(id) === index)
}

/* -------------------------------------------------------------------- bid */

/** `x.y% under/over` the calculation price, exact to one decimal (halves away from zero). */
export function bidVersusPrice(maxBidPence: string | null, pricePence: number | null | undefined): { percent: string; direction: 'under' | 'over' | 'at' } | null {
  if (!maxBidPence || !/^\d+$/.test(maxBidPence) || pricePence === null || pricePence === undefined || !Number.isSafeInteger(pricePence) || pricePence <= 0) return null
  const price = BigInt(pricePence), bid = BigInt(maxBidPence)
  const difference = price - bid
  if (difference === 0n) return { percent: '0.0%', direction: 'at' }
  const magnitude = difference < 0n ? -difference : difference
  const tenths = (magnitude * 2000n + price) / (2n * price)
  return { percent: `${(tenths / 10n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${tenths % 10n}%`, direction: difference > 0n ? 'under' : 'over' }
}

export interface BidConstraint { label: string; value: string }

/**
 * The limiting constraint: the solver's constraint row as it reads one penny
 * above the maximum (the evaluation's `limitingConstraint`), else, when the
 * calculation price itself fails, the same row at that price.
 */
export function bidConstraint(layout: DealModelLayout, lookups: ResultLookups, evaluation: BidEvaluation): BidConstraint | null {
  const id = layout.solver?.constraintOutputId ?? null
  const output = id ? lookups.outputs.get(id) : undefined
  const atPrice = id ? lookups.value(id) : undefined
  const value = evaluation.bid.limitingConstraint ?? (evaluation.bid.passesAtMarketedPrice === false && typeof atPrice === 'string' ? atPrice : null)
  return value ? { label: output?.label ?? 'Limited by', value: humanize(value) } : null
}

export function bidSentence(evaluation: BidEvaluation, priceLabel: string, solverLabel = 'Maximum sensible bid'): string {
  const { bid } = evaluation
  const subject = solverLabel.toLowerCase()
  if (bid.state !== 'computed' || bid.maxBidPence === null) return `${bidStateLabel(bid.state)}: no ${subject} is available.`
  const amount = formatBidPence(bid.maxBidPence)
  const price = priceLabel.toLowerCase()
  if (bid.passesAtMarketedPrice === true) return `Bid-eligible at the ${price}: the ${subject} is ${amount}.`
  if (bid.passesAtMarketedPrice === false) return `Not a bid at the ${price}: the ${subject} is ${amount}.`
  return `The ${subject} is ${amount}; the ${price} is unknown.`
}

/* --------------------------------------------- Adjust rail draft values */

export type DraftValue = string | boolean | null
export type EditKind = 'toggle' | 'select' | 'pence' | 'money' | 'percent' | 'percentage' | 'integer' | 'number' | 'text'

export function editKind(input: DealModelLayoutInput): EditKind {
  const kind = input.semanticType.kind
  if (input.control === 'toggle' || kind === 'boolean') return 'toggle'
  if (input.control === 'select' || kind === 'enum') return 'select'
  if (isPenceInput(input)) return 'pence'
  if (kind === 'money') return 'money'
  if (kind === 'ratio') return 'percent'
  if (kind === 'percentage') return 'percentage'
  if (INTEGRAL_KINDS.has(kind)) return 'integer'
  if (kind === 'decimal' || kind === 'areaSqm') return 'number'
  return 'text'
}

/** The editable text (or boolean) for a value in the input's own unit: pence and ratios show as pounds and percent. */
export function tokenToDraft(input: DealModelLayoutInput, token: OutputScalar | undefined): DraftValue {
  const kind = editKind(input)
  if (token === undefined || token === null) return kind === 'toggle' || kind === 'select' ? null : ''
  if (kind === 'toggle') return typeof token === 'boolean' ? token : null
  if (typeof token !== 'string') return ''
  if (kind === 'select' || kind === 'text') return token
  if (kind === 'pence') return shiftSettingsDecimal(token, -2) ?? token
  if (kind === 'percent') return shiftSettingsDecimal(token, 2) ?? token
  return normalizeSettingsDecimal(token) ?? token
}

/**
 * `clear`: an emptied field on a nullable input stages no value of its own
 * (the saved override, if any, is removed); only an explicit "Not set"
 * choice (`null`) stores a null override.
 */
export type DraftResult = { ok: true; value: string | boolean | null } | { ok: true; clear: true } | { ok: false; message: string }

/** The exact override value for a draft: pounds as decimal text, *Pence as integer pence, ratios from percent. */
export function draftToOverride(input: DealModelLayoutInput, draft: DraftValue): DraftResult {
  const kind = editKind(input)
  const nullable = input.semanticType.nullable
  if (draft === null) return nullable ? { ok: true, value: null } : { ok: false, message: 'Enter a value.' }
  if (typeof draft === 'string' && draft.trim() === '' && kind !== 'text') return nullable ? { ok: true, clear: true } : { ok: false, message: 'Enter a value.' }
  if (kind === 'toggle') return typeof draft === 'boolean' ? { ok: true, value: draft } : { ok: false, message: 'Choose yes or no.' }
  if (typeof draft !== 'string') return { ok: false, message: 'Enter a value.' }
  if (kind === 'select') return (input.semanticType.enumValues ?? []).includes(draft) ? { ok: true, value: draft } : { ok: false, message: 'Choose one of the listed values.' }
  if (kind === 'text') return { ok: true, value: draft }
  const canonical = normalizeSettingsDecimal(draft)
  if (canonical === null) return { ok: false, message: 'Enter a number.' }
  const fraction = canonical.split('.')[1] ?? ''
  if (kind === 'pence' || kind === 'money') {
    if (fraction.length > 2) return { ok: false, message: 'Use at most two decimal places.' }
    return { ok: true, value: kind === 'pence' ? shiftSettingsDecimal(canonical, 2) ?? canonical : canonical }
  }
  if (kind === 'integer') return fraction ? { ok: false, message: 'Enter a whole number.' } : { ok: true, value: canonical }
  if (kind === 'percent') { const ratio = shiftSettingsDecimal(canonical, -2); return ratio === null ? { ok: false, message: 'Enter a number.' } : { ok: true, value: ratio } }
  return { ok: true, value: canonical }
}
